import { useEffect, useState } from "react";
import { X, Send, MessageSquare, Mail, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { I18nBadge, LanguageSelect, useActiveLanguages } from "@/components/email/EmailI18n";

/**
 * Diálogo "Enviar aviso al cliente" con 3 pasos:
 *   1) Tipo de aviso (CON o SIN reparación)
 *   2) Canal (WhatsApp → wa.me, o Email)
 *   3) Vista previa editable → Enviar (+ confirmación de cambio de estado)
 *
 * Para WhatsApp abre wa.me con el texto prellenado; no envía automáticamente.
 * Para Email lo manda vía SMTP del backend.
 */
export default function NotifyCustomerDialog({ repairId, customer, currentStatus, onClose, onSent }) {
  // Paso: kind → channel → preview
  const [step, setStep] = useState("kind");
  const [kind, setKind] = useState(null);      // 'with_repair' | 'without_repair'
  const [channel, setChannel] = useState(null); // 'whatsapp' | 'email'
  const [preview, setPreview] = useState(null); // { subject, body, to, wa_link }
  const [body, setBody] = useState("");        // texto editable
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [lang, setLang] = useState("");      // override puntual del idioma (solo email)
  const languages = useActiveLanguages();

  const targetStatus =
    kind === "with_repair" ? "listo_para_entregar"
    : kind === "without_repair" ? "listo_sin_reparacion"
    : "";  // received_no_quote: no cambia estado
  const targetStatusLabel =
    kind === "with_repair" ? "Listo para entregar"
    : kind === "without_repair" ? "Listo (sin reparación)"
    : "";
  const willOfferStatusChange = kind && targetStatus && currentStatus !== targetStatus;

  // Carga la vista previa cuando llegamos al paso 3
  useEffect(() => {
    if (step !== "preview" || !kind || !channel) return;
    setLoading(true);
    api
      .post(`/repairs/${repairId}/notify/preview`, { kind, channel, language_code: lang || null })
      .then((r) => {
        setPreview(r.data);
        setBody(r.data.body);
        if (!lang && r.data.i18n?.requested_language) setLang(r.data.i18n.requested_language);
      })
      .catch((err) => {
        toast.error(formatApiError(err));
        setStep("channel");
      })
      .finally(() => setLoading(false));
  }, [step, kind, channel, repairId, lang]); // eslint-disable-line

  const phone = (customer?.phone || "").trim();
  const email = (customer?.email || "").trim();

  const goChannel = (k) => { setKind(k); setStep("channel"); };
  const goPreview = (c) => {
    if (c === "whatsapp" && !phone) {
      toast.error("Este cliente no tiene teléfono registrado.");
      return;
    }
    if (c === "email" && !email) {
      toast.error("Este cliente no tiene email registrado.");
      return;
    }
    setChannel(c); setStep("preview");
  };

  const send = async (changeStatus = false) => {
    setSending(true);
    try {
      const { data } = await api.post(`/repairs/${repairId}/notify`, {
        kind, channel, override_text: channel === "email" ? undefined : body, change_status: changeStatus,
        language_code: channel === "email" ? (lang || null) : null,
      });
      if (channel === "whatsapp" && data.wa_link) {
        // Abrir WhatsApp Web/App con el texto ya preparado en nueva pestaña
        window.open(data.wa_link, "_blank", "noopener,noreferrer");
        toast.success("WhatsApp abierto con el mensaje preparado.");
      } else {
        toast.success("Email enviado correctamente.");
      }
      onSent?.(data.repair);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const handleSendClick = () => {
    if (!willOfferStatusChange) return send(false);
    // Pregunta al usuario si quiere cambiar el estado tras enviar
    const ok = window.confirm(
      `¿Cambiar el estado de la reparación a "${targetStatusLabel}" tras enviar el aviso?`
    );
    send(ok);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-[#111110] shadow-hard w-full max-w-2xl max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="notify-customer-dialog"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E0] sticky top-0 bg-white">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
              // {step === "kind" ? "Paso 1 de 3" : step === "channel" ? "Paso 2 de 3" : "Paso 3 de 3"}
            </div>
            <div className="font-display font-bold text-base">Enviar aviso al cliente</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F7F4]" data-testid="notify-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {step === "kind" && (
            <KindStep onPick={goChannel} />
          )}

          {step === "channel" && (
            <ChannelStep
              kind={kind}
              phone={phone}
              email={email}
              onBack={() => setStep("kind")}
              onPick={goPreview}
            />
          )}

          {step === "preview" && (
            <PreviewStep
              kind={kind}
              channel={channel}
              loading={loading}
              preview={preview}
              body={body}
              setBody={setBody}
              onBack={() => setStep("channel")}
              onSend={handleSendClick}
              sending={sending}
              willOfferStatusChange={willOfferStatusChange}
              targetStatusLabel={targetStatusLabel}
              lang={lang}
              setLang={setLang}
              languages={languages}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Steps ----------

function KindStep({ onPick }) {
  return (
    <div className="space-y-3">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
        ¿Qué tipo de aviso quieres enviar?
      </div>
      <button
        onClick={() => onPick("with_repair")}
        data-testid="notify-kind-with"
        className="w-full text-left border border-[#E5E5E0] hover:border-[#6E8B3D] hover:bg-[#FAFAF8] p-4 transition-colors"
      >
        <div className="font-bold text-sm text-[#111110]">Equipo listo para recoger — CON reparación</div>
        <div className="text-xs text-[#525252] mt-1">
          Avisa al cliente de que su equipo está reparado y listo para recoger. Incluye importe total.
        </div>
      </button>
      <button
        onClick={() => onPick("without_repair")}
        data-testid="notify-kind-without"
        className="w-full text-left border border-[#E5E5E0] hover:border-[#6E8B3D] hover:bg-[#FAFAF8] p-4 transition-colors"
      >
        <div className="font-bold text-sm text-[#111110]">Equipo listo para recoger — SIN reparación</div>
        <div className="text-xs text-[#525252] mt-1">
          Avisa al cliente de que su equipo está listo para recoger pero finalmente no se ha reparado.
        </div>
      </button>
      <button
        onClick={() => onPick("received_no_quote")}
        data-testid="notify-kind-received"
        className="w-full text-left border border-[#E5E5E0] hover:border-[#6E8B3D] hover:bg-[#FAFAF8] p-4 transition-colors"
      >
        <div className="font-bold text-sm text-[#111110]">Recepción del equipo — SIN presupuesto</div>
        <div className="text-xs text-[#525252] mt-1">
          Avisa al cliente de que hemos recibido su equipo y que queda pendiente de revisión/presupuesto.
        </div>
      </button>
    </div>
  );
}

function ChannelStep({ kind, phone, email, onBack, onPick }) {
  return (
    <div className="space-y-3">
      <div className="text-xs text-[#525252]">
        Tipo: <strong>{kind === "with_repair" ? "CON reparación" : "SIN reparación"}</strong>
      </div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
        Canal de envío
      </div>
      <button
        onClick={() => onPick("whatsapp")}
        disabled={!phone}
        data-testid="notify-channel-whatsapp"
        className="w-full text-left border border-[#E5E5E0] hover:border-[#6E8B3D] hover:bg-[#FAFAF8] p-4 transition-colors disabled:opacity-50 disabled:hover:border-[#E5E5E0] disabled:cursor-not-allowed"
      >
        <div className="flex items-center gap-2 font-bold text-sm">
          <MessageSquare size={14} className="text-[#25D366]" /> WhatsApp
        </div>
        <div className="text-xs text-[#525252] mt-1">
          {phone ? <>Se abrirá WhatsApp con el mensaje listo para enviar a <span className="font-mono">{phone}</span></>
                 : <span className="inline-flex items-center gap-1 text-amber-700"><AlertCircle size={12} /> El cliente no tiene teléfono registrado.</span>}
        </div>
      </button>
      <button
        onClick={() => onPick("email")}
        disabled={!email}
        data-testid="notify-channel-email"
        className="w-full text-left border border-[#E5E5E0] hover:border-[#6E8B3D] hover:bg-[#FAFAF8] p-4 transition-colors disabled:opacity-50 disabled:hover:border-[#E5E5E0] disabled:cursor-not-allowed"
      >
        <div className="flex items-center gap-2 font-bold text-sm">
          <Mail size={14} className="text-[#6E8B3D]" /> Email
        </div>
        <div className="text-xs text-[#525252] mt-1">
          {email ? <>Se enviará a <span className="font-mono">{email}</span></>
                 : <span className="inline-flex items-center gap-1 text-amber-700"><AlertCircle size={12} /> El cliente no tiene email registrado.</span>}
        </div>
      </button>
      <div className="pt-2">
        <button onClick={onBack} data-testid="notify-back-kind" className="text-xs text-[#525252] hover:text-[#111110]">
          ← Volver
        </button>
      </div>
    </div>
  );
}

function PreviewStep({ kind, channel, loading, preview, body, setBody, onBack, onSend, sending, willOfferStatusChange, targetStatusLabel, lang, setLang, languages }) {
  return (
    <div className="space-y-3">
      <div className="text-xs text-[#525252] flex flex-wrap gap-x-4 gap-y-1">
        <span>Tipo: <strong>{kind === "with_repair" ? "CON reparación" : "SIN reparación"}</strong></span>
        <span>Canal: <strong>{channel === "whatsapp" ? "WhatsApp" : "Email"}</strong></span>
        {preview?.to && <span>Para: <span className="font-mono">{preview.to}</span></span>}
      </div>
      {channel === "email" && preview?.i18n && (
        <div className="flex flex-wrap items-center justify-between gap-2 border border-[#E5E5E0] bg-[#FAFAF8] px-3 py-2">
          <I18nBadge i18n={preview.i18n} testid="notify-i18n-badge" />
          <LanguageSelect value={lang || preview.i18n.requested_language} onChange={setLang} languages={languages} customerLanguage={preview.customer_language} testid="notify-language-select" />
        </div>
      )}

      {loading ? (
        <div className="py-8 text-center text-sm text-[#525252]">Generando vista previa…</div>
      ) : (
        <>
          {channel === "email" && preview?.subject && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1">Asunto</div>
              <div className="px-3 py-2 border border-[#E5E5E0] bg-[#F7F7F4] text-sm">{preview.subject}</div>
            </div>
          )}
          {channel === "email" ? (
            // Para email: NO mostrar el HTML. Vista previa renderizada + nota.
            // La edición del contenido SÓLO se hace en Configuración > Diseño > Emails.
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1">
                Vista previa del email
              </div>
              <div className="border border-[#E5E5E0] bg-white">
                <iframe
                  title="Vista previa email"
                  srcDoc={body}
                  className="w-full h-80 bg-white"
                  data-testid="notify-email-iframe"
                />
              </div>
              <div className="mt-2 text-[11px] text-[#525252] bg-[#FAFAF8] border border-[#E5E5E0] px-3 py-2">
                Para editar el contenido de la plantilla ve a{" "}
                <strong>Configuración &gt; Diseño &gt; Emails</strong>.
              </div>
            </div>
          ) : (
            // Para WhatsApp es texto plano → editable directamente.
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                  Mensaje (texto plano)
                </div>
                <div className="text-[10px] text-[#525252]">Puedes editarlo solo para este envío.</div>
              </div>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={10}
                data-testid="notify-body"
                className="w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm font-mono rounded-none"
              />
            </div>
          )}
          {willOfferStatusChange && (
            <div className="text-xs text-[#525252] bg-[#FAFAF8] border border-[#E5E5E0] px-3 py-2">
              Tras enviar te preguntaré si quieres cambiar el estado a <strong>{targetStatusLabel}</strong>.
            </div>
          )}
        </>
      )}

      <div className="flex items-center justify-between pt-2">
        <button onClick={onBack} data-testid="notify-back-channel" className="text-xs text-[#525252] hover:text-[#111110]">
          ← Volver
        </button>
        <button
          onClick={onSend}
          disabled={sending || loading || !body.trim()}
          data-testid="notify-send"
          className="inline-flex items-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
        >
          <Send size={14} />
          {sending ? "Enviando…" : (channel === "whatsapp" ? "Abrir WhatsApp" : "Enviar email")}
        </button>
      </div>
    </div>
  );
}
